import { validationLib } from "@agentcall/core/lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useEffect, useMemo, useState } from "react"
import { AlertTriangle, Edit, MoreHorizontal, Plus, Trash } from "react-feather"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { adminApi } from "@/api/client/admin"
import type { AdminList200AdminsItem, AdminListParams } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAdminRoles } from "@/features/admin-user"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { useOrganizationOptions } from "@/features/organization"
import { toast } from "@/hooks/use-toast"

// 検索フォーム用のスキーマ
const searchFormSchema = z.object({
  adminUserId: z.string().optional(),
  name: z.string().optional(),
  nameKana: z.string().optional(),
  role: z.string().optional(),
})

type SearchFormValues = z.infer<typeof searchFormSchema>

// 追加フォーム用のスキーマ
const addAdminUserFormSchema = z.object({
  organizationId: z.string().min(1, "組織を選択してください"),
  role: z.string().min(1, "権限の種類を選択してください"),
  email: z.string().email("有効なメールアドレスを入力してください"),
  password: z
    .string()
    .min(8, "パスワードは8文字以上で入力してください")
    .regex(/^[0-9A-Za-z]+$/, "半角英数字のみで入力してください"),
  name: z.string().min(1, "氏名を入力してください"),
  nameKana: validationLib.katakanaOptionalZ,
  department: z.string().optional(),
})

// 編集フォーム用のスキーマ
const editAdminUserFormSchema = z.object({
  email: z.string().email("有効なメールアドレスを入力してください"),
  password: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 8, {
      message: "パスワードは8文字以上で入力してください",
    })
    .refine((val) => !val || /^[0-9A-Za-z]+$/.test(val), {
      message: "半角英数字のみで入力してください",
    }),
  name: z.string().min(1, "氏名を入力してください"),
  nameKana: validationLib.katakanaOptionalZ,
  department: z.string().optional(),
})

type AddAdminUserFormValues = z.infer<typeof addAdminUserFormSchema>
type EditAdminUserFormValues = z.infer<typeof editAdminUserFormSchema>

// nuqsのパーサー定義
const adminUsersPageParsers = {
  page: parseAsInteger.withDefault(1),
  adminUserId: parseAsString,
  name: parseAsString,
  nameKana: parseAsString,
  role: parseAsString,
}

export function AdminUsersPage() {
  const itemsPerPage = 20
  const queryClient = useQueryClient()
  const { options: roleOptions } = useAdminRoles()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(adminUsersPageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      adminUserId: queryParams.adminUserId || "",
      name: queryParams.name || "",
      nameKana: queryParams.nameKana || "",
      role: queryParams.role || "",
    } as SearchFormValues
  }, [queryParams])

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      adminUserId: queryParams.adminUserId || "",
      name: queryParams.name || "",
      nameKana: queryParams.nameKana || "",
      role: queryParams.role || "",
    },
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1, // 検索時はページを1にリセット
      adminUserId: formData.adminUserId || null,
      name: formData.name || null,
      nameKana: formData.nameKana || null,
      role: formData.role || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/admins"] })
  }

  const onReset = () => {
    form.reset()
    // URLパラメーターをクリア
    setQueryParams({
      page: null,
      adminUserId: null,
      name: null,
      nameKana: null,
      role: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">管理ユーザー一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="adminUserId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>管理ユーザーID</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>氏名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nameKana"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>氏名（カナ）</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>管理者権限</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="選択してください"
                          options={roleOptions}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default">
                  検索
                </Button>
                <Button type="button" variant="outline" onClick={onReset}>
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<AdminUsersPageFallback />}>
            <AdminUsersPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface AdminUsersPageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

function AdminUsersPageContent({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: AdminUsersPageContentProps) {
  const { canAccess, canCreate, canUpdate, canDelete } = usePermission()
  const { getLabel: getRoleLabel } = useAdminRoles()
  const canCreateAdmin = canCreate("admin_users")
  const canLoadCreationOptions = canAccess("organizations") && canAccess("roles")

  // モーダル状態管理
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminList200AdminsItem | null>(null)

  const handleOpenEditModal = (adminUser: AdminList200AdminsItem) => {
    setSelectedUser(adminUser)
    setIsEditModalOpen(true)
  }

  const handleOpenDeleteModal = (adminUser: AdminList200AdminsItem) => {
    setSelectedUser(adminUser)
    setIsDeleteModalOpen(true)
  }

  const handleCloseModals = () => {
    setIsAddModalOpen(false)
    setIsEditModalOpen(false)
    setIsDeleteModalOpen(false)
    setSelectedUser(null)
  }

  // APIパラメータを構築
  const apiParams = useMemo(() => {
    const params: AdminListParams = {
      page: currentPage,
      limit: itemsPerPage,
    }

    if (searchParams?.adminUserId) {
      params.id = searchParams.adminUserId
    }
    if (searchParams?.name) {
      params.nameLike = searchParams.name
    }
    if (searchParams?.nameKana) {
      params.nameKanaLike = searchParams.nameKana
    }
    if (searchParams?.role) {
      params.role = searchParams.role
    }

    return params
  }, [searchParams, currentPage, itemsPerPage])

  // APIリクエスト
  const { data, refetch } = adminApi.useAdminListSuspense(apiParams)

  // ページネーション計算
  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / itemsPerPage)
  const adminUsers = data.admins

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  const getStatusLabel = (banned: boolean | null) => {
    return banned ? "一時停止" : "使用中"
  }

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{totalCount}件の管理ユーザーが見つかりました</div>
        {canCreateAdmin && (
          <div className="flex flex-col items-end gap-1">
            <Button
              onClick={() => setIsAddModalOpen(true)}
              disabled={!canLoadCreationOptions}
              title={
                canLoadCreationOptions
                  ? undefined
                  : "管理ユーザーの追加には、組織管理とロール管理の閲覧権限が必要です"
              }
            >
              <Plus className="mr-2" size={16} />
              管理ユーザーを追加
            </Button>
            {!canLoadCreationOptions && (
              <p className="text-xs text-slate-500">
                追加には組織管理・ロール管理の閲覧権限が必要です
              </p>
            )}
          </div>
        )}
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">管理ユーザーID</TableHead>
              <TableHead className="whitespace-nowrap">氏名</TableHead>
              <TableHead className="whitespace-nowrap">氏名（カナ）</TableHead>
              <TableHead className="whitespace-nowrap">管理者権限</TableHead>
              <TableHead className="whitespace-nowrap">組織</TableHead>
              <TableHead className="whitespace-nowrap">部署名</TableHead>
              <TableHead className="whitespace-nowrap">メールアドレス</TableHead>
              <TableHead className="whitespace-nowrap">ステータス</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {adminUsers.map((adminUser) => (
              <TableRow key={adminUser.id}>
                <TableCell>{adminUser.id}</TableCell>
                <TableCell
                  className={
                    canUpdate("admin_users") ? "text-blue-600 cursor-pointer hover:underline" : ""
                  }
                  onClick={
                    canUpdate("admin_users") ? () => handleOpenEditModal(adminUser) : undefined
                  }
                >
                  {adminUser.name}
                </TableCell>
                <TableCell>{adminUser.nameKana || "-"}</TableCell>
                <TableCell>{getRoleLabel(adminUser.role, adminUser.organizationName)}</TableCell>
                <TableCell>{adminUser.organizationName || "-"}</TableCell>
                <TableCell>{adminUser.department || "-"}</TableCell>
                <TableCell>{adminUser.email}</TableCell>
                <TableCell>{getStatusLabel(adminUser.banned)}</TableCell>
                <TableCell>
                  {(canUpdate("admin_users") || canDelete("admin_users")) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[120px]">
                        {canUpdate("admin_users") && (
                          <DropdownMenuItem onClick={() => handleOpenEditModal(adminUser)}>
                            <Edit className="text-slate-600 mr-2" size={16} />
                            編集
                          </DropdownMenuItem>
                        )}
                        {canUpdate("admin_users") && canDelete("admin_users") && (
                          <DropdownMenuSeparator />
                        )}
                        {canDelete("admin_users") && (
                          <DropdownMenuItem
                            onClick={() => handleOpenDeleteModal(adminUser)}
                            className="text-red-600"
                          >
                            <Trash className="text-red-400 mr-2" size={16} />
                            削除
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {adminUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  管理ユーザーが見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-slate-500">
            {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
            {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationFirst
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationPrevious
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage - 1)
                  }}
                  disabled={currentPage === 1}
                  className="cursor-pointer"
                />
              </PaginationItem>
              {renderPageNumbers()}
              <PaginationItem>
                <PaginationNext
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(currentPage + 1)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLast
                  onClick={(e) => {
                    e.preventDefault()
                    handlePageChange(totalPages)
                  }}
                  disabled={currentPage === totalPages}
                  className="cursor-pointer"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* 追加モーダル */}
      {isAddModalOpen && canLoadCreationOptions && (
        <AddAdminUserModal
          isOpen={isAddModalOpen}
          onClose={handleCloseModals}
          onRefetch={refetch}
        />
      )}

      {/* 編集モーダル */}
      <EditAdminUserModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModals}
        adminUser={selectedUser}
        onRefetch={refetch}
      />

      {/* 削除確認モーダル */}
      <DeleteAdminUserModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        adminUser={selectedUser}
        onRefetch={refetch}
      />
    </div>
  )
}

// 追加モーダルコンポーネント
interface AddAdminUserModalProps {
  isOpen: boolean
  onClose: () => void
  onRefetch: () => void
}

function AddAdminUserModal({ isOpen, onClose, onRefetch }: AddAdminUserModalProps) {
  const { getOptionsForOrganization } = useAdminRoles()
  const {
    options: organizationOptions,
    isPending: isOrganizationPending,
    isError: isOrganizationError,
    refetch: refetchOrganizations,
  } = useOrganizationOptions()

  const form = useForm<AddAdminUserFormValues>({
    resolver: zodResolver(addAdminUserFormSchema),
    defaultValues: {
      organizationId: "",
      role: "",
      email: "",
      password: "",
      name: "",
      nameKana: "",
      department: "",
    },
  })

  const organizationId = form.watch("organizationId")
  const roleOptions = useMemo(
    () => (organizationId ? getOptionsForOrganization(organizationId) : []),
    [organizationId, getOptionsForOrganization]
  )

  const createAdminMutation = adminApi.useAdminCreate({
    mutation: {
      onError: (error) =>
        toast({
          title: "管理ユーザーの追加に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })

  const onSubmit = (data: AddAdminUserFormValues) => {
    createAdminMutation.mutate(
      {
        data: {
          organizationId: data.organizationId,
          email: data.email,
          password: data.password,
          name: data.name,
          nameKana: data.nameKana || null,
          department: data.department || null,
          role: data.role,
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "管理ユーザーを追加しました",
          })
          form.reset()
          onRefetch()
          onClose()
        },
      }
    )
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>管理ユーザーの追加</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>氏名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="山田 太郎" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameKana"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>氏名（カナ）</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="ヤマダ タロウ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>部署名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="営業部" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organizationId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>組織</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value)
                        form.resetField("role")
                      }}
                      placeholder={isOrganizationPending ? "読み込み中..." : "選択してください"}
                      options={organizationOptions}
                      disabled={isOrganizationPending || isOrganizationError}
                    />
                  </FormControl>
                  {isOrganizationError && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <span>組織一覧を取得できませんでした。</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => refetchOrganizations()}
                      >
                        再読み込み
                      </Button>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>権限の種類</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={
                        organizationId ? "選択してください" : "先に組織を選択してください"
                      }
                      options={roleOptions}
                      disabled={!organizationId}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>メールアドレス</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="tanaka@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>パスワード</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" variant="default" disabled={createAdminMutation.isPending}>
                {createAdminMutation.isPending ? "追加中..." : "発行する"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={createAdminMutation.isPending}
              >
                キャンセル
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// 編集モーダルコンポーネント
interface EditAdminUserModalProps {
  isOpen: boolean
  onClose: () => void
  adminUser: AdminList200AdminsItem | null
  onRefetch: () => void
}

function EditAdminUserModal({ isOpen, onClose, adminUser, onRefetch }: EditAdminUserModalProps) {
  const form = useForm<EditAdminUserFormValues>({
    resolver: zodResolver(editAdminUserFormSchema),
    defaultValues: {
      email: adminUser?.email || "",
      password: "",
      name: adminUser?.name || "",
      nameKana: adminUser?.nameKana || "",
      department: adminUser?.department || "",
    },
  })

  // adminUserが変更されたらフォームをリセット
  useEffect(() => {
    if (adminUser) {
      form.reset({
        email: adminUser.email,
        password: "",
        name: adminUser.name,
        nameKana: adminUser.nameKana || "",
        department: adminUser.department || "",
      })
    }
  }, [adminUser, form])

  const updateAdminMutation = adminApi.useAdminUpdate({
    mutation: {
      onError: (error) =>
        toast({
          title: "管理ユーザーの更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })

  const onSubmit = (data: EditAdminUserFormValues) => {
    if (!adminUser) return

    updateAdminMutation.mutate(
      {
        id: adminUser.id,
        data: {
          name: data.name,
          nameKana: data.nameKana || null,
          department: data.department || null,
          email: data.email,
          ...(data.password ? { password: data.password } : {}),
        },
      },
      {
        onSuccess: () => {
          toast({
            title: "管理ユーザーを更新しました",
          })
          onRefetch()
          onClose()
        },
      }
    )
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>管理ユーザーの編集</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>氏名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="山田 太郎" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameKana"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>氏名（カナ）</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="ヤマダ タロウ" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>部署名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="営業部" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>メールアドレス</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="tanaka@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>パスワード</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="変更する場合のみ入力" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" variant="default" disabled={updateAdminMutation.isPending}>
                {updateAdminMutation.isPending ? "保存中..." : "保存する"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateAdminMutation.isPending}
              >
                キャンセル
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// 削除確認モーダルコンポーネント
interface DeleteAdminUserModalProps {
  isOpen: boolean
  onClose: () => void
  adminUser: AdminList200AdminsItem | null
  onRefetch: () => void
}

function DeleteAdminUserModal({
  isOpen,
  onClose,
  adminUser,
  onRefetch,
}: DeleteAdminUserModalProps) {
  const deleteAdminMutation = adminApi.useAdminDelete({
    mutation: {
      onError: (error) =>
        toast({
          title: "削除に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })

  const handleDelete = () => {
    if (!adminUser) return

    deleteAdminMutation.mutate(
      { id: adminUser.id },
      {
        onSuccess: () => {
          toast({
            title: "削除しました",
          })
          // 一覧データを再取得
          onRefetch()
          onClose()
        },
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-red-500" size={20} />
            本当に削除してよろしいですか？
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>削除したユーザーは復元できません。</DialogDescription>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={deleteAdminMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteAdminMutation.isPending}
          >
            {deleteAdminMutation.isPending ? "削除中..." : "削除する"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AdminUsersPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
